import { useHtmlDir } from '@aboutyou/legacy-shared/src/LegacyWebCoreConfigContext'
import { useHover } from 'helpers/hooks/useHover'
import React from 'react'
import styled, { css } from 'styled-components'

import ProductImage from '../ProductImage/ProductImage'
import ProductTileWishlistButton from './ProductTileWishlistButton'

interface IHoverImageProps {
  active: boolean
  padded: boolean
}

export const HoverImage = styled(ProductImage)`
  position: absolute;
  top: 8px;
  right: 8px;
  left: 8px;
  bottom: 8px;
  opacity: 0;
  width: calc(100% - 16px);
  padding-top: calc(133% - 16px);
  transform: translateY(3px);
  transition: opacity 300ms ease-in, transform 250ms ease-in;
  ${({ active }: IHoverImageProps) =>
    !active
      ? css`
          opacity: 0 !important;
          transform: translateY(3px) !important;
          animation: none !important;
        `
      : null};
`

const MemoHoverImage = React.memo(HoverImage)

interface IProps {
  imgTestId?: string
  src?: string
  srcSet?: string
  sizes?: string
  padded: boolean
  hoverRef: React.RefObject<HTMLAnchorElement | HTMLElement>
  wishlisted?: boolean
  reducedPaddingTop?: boolean
  onWishlistClick?: (ev: React.MouseEvent) => Promise<void> | void
  alt?: string
}

const ProductTileHover: React.FC<IProps> = ({
  imgTestId = 'ProductImageHover',
  src,
  srcSet,
  sizes,
  hoverRef,
  padded,
  onWishlistClick,
  wishlisted,
  reducedPaddingTop,
  alt,
}) => {
  const { shouldRender, isActive, onLoad } = useHoverImage(hoverRef, src)
  const isRTL = useHtmlDir() === 'rtl'
  const wishlistClickHandler = React.useCallback(
    (ev: React.MouseEvent) => {
      ev.stopPropagation()
      if (onWishlistClick) {
        onWishlistClick(ev)
      }
    },
    [onWishlistClick],
  )

  return (
    <>
      {src && shouldRender && (
        <MemoHoverImage
          dataTestId={imgTestId}
          src={src}
          srcSet={srcSet}
          sizes={sizes}
          onLoad={onLoad}
          active={isActive}
          padded={padded}
          reducedPaddingTop={reducedPaddingTop}
          alt={alt}
        />
      )}
      <ProductTileWishlistButton
        onClick={wishlistClickHandler}
        filled={wishlisted}
        grey={!wishlisted}
        $isRTL={isRTL}
      />
    </>
  )
}

const useHoverImage = (
  ref: React.RefObject<HTMLAnchorElement | HTMLElement>,
  src?: string,
) => {
  const hasImage = Boolean(src)
  const [isActive, setLoaded] = React.useState(false)
  const { mouseOver } = useHover<HTMLAnchorElement | HTMLElement>(
    ref,
    {
      once: isActive,
    },
    [src],
  )
  const activate = React.useCallback(() => {
    requestAnimationFrame(() => setLoaded(true))
  }, [setLoaded])

  React.useEffect(() => {
    if (mouseOver && !hasImage) {
      activate()
    }
  }, [mouseOver, hasImage, activate])

  return {
    shouldRender: mouseOver || isActive,
    isActive,
    onLoad: activate,
  }
}

export default ProductTileHover
